import { useState } from "react";
import { useContext } from "react";
import { AuthContext } from "../../../shared/context/auth-context";
import { ClinicaContext } from "../../../shared/context/clinica-context";
import { useHttpClient } from "../../../shared/hooks/http-hook";
import toast from "react-hot-toast";
import "./UnirClientes.scss";
import { useLoading } from "../../../shared/context/LoadingContext";

// Extrai um ObjectId de uma string que pode ser:
//   - um ObjectId puro (24 hex chars)
//   - uma URL completa do tipo "https://app.example.com/clientes/507f1f77..."
const extrairId = (valor) => {
  if (!valor) return null;
  const trimmed = valor.trim();
  // Padrão: /clientes/<id> — extrai o segmento após /clientes/
  const match = trimmed.match(/\/clientes\/([a-f0-9]{24})/i);
  if (match) return match[1];
  // Fallback: pode já ser só o ID
  if (/^[a-f0-9]{24}$/i.test(trimmed)) return trimmed;
  return null;
};

function UnirClientes() {
  const auth = useContext(AuthContext);
  const clinicaCtx = useContext(ClinicaContext);
  const { sendRequest } = useHttpClient();
  const [isLoading, setIsLoading] = useState(false);
  const [clienteAManter, setClienteAManter] = useState("");
  const [clienteAEliminar, setClienteAEliminar] = useState("");
  const [resultado, setResultado] = useState(null);
  const [erroValidacao, setErroValidacao] = useState(null);
  const [confirmando, setConfirmando] = useState(false);

  const idManter = extrairId(clienteAManter);
  const idEliminar = extrairId(clienteAEliminar);
  const { startLoading, stopLoading } = useLoading();

  const validar = () => {
    if (!clienteAManter.trim() || !clienteAEliminar.trim()) {
      return "Preenche os dois campos.";
    }
    if (!idManter) {
      return "O campo 'Cliente a manter' não contém um ID válido.";
    }
    if (!idEliminar) {
      return "O campo 'Cliente a eliminar' não contém um ID válido.";
    }
    if (idManter === idEliminar) {
      return "Os dois clientes não podem ser o mesmo.";
    }
    return null;
  };

  const handlePrepararConfirmacao = () => {
    const erro = validar();
    setErroValidacao(erro);
    if (!erro) {
      setConfirmando(true);
      setResultado(null);
    }
  };

  const handleCancelar = () => {
    setConfirmando(false);
    setErroValidacao(null);
  };

  const handleCorrer = async () => {
    try {
      setIsLoading(true);
      startLoading();
      const resposta = await sendRequest(
        `${process.env.REACT_APP_BACKEND_LINK}/api/manutencoes/unir`,
        "POST",
        JSON.stringify({
          clienteAManter: idManter,
          clienteAEliminar: idEliminar,
          clinica: clinicaCtx.clinica._id,
        }),
        {
          "Content-Type": "application/json",
          Authorization: "Bearer " + auth.token,
        },
      );

      setResultado({ sucesso: true, dados: resposta });
      setConfirmando(false);
      setClienteAManter("");
      setClienteAEliminar("");
      setIsLoading(false);
      toast.success("Clientes unidos com sucesso.");
    } catch (err) {
      setResultado({
        sucesso: false,
        mensagem: err.message || "Erro desconhecido.",
      });
      setConfirmando(false);
    } finally {
      stopLoading();
    }
  };

  return (
    <div className="unir-clientes">
      {/* ── Explicação ──────────────────────────────────────────────── */}
      <div className="unir-clientes__explicacao">
        <h3 className="unir-clientes__explicacao__titulo">
          O que faz este script?
        </h3>
        <p>
          Este script <strong>unifica dois registos de cliente</strong> numa só
          ficha. É útil quando o mesmo cliente foi registado duas vezes na
          clínica e existem dados distribuídos pelos dois registos.
        </p>

        <h4 className="unir-clientes__explicacao__subtitulo">
          O que é transferido
        </h4>
        <ul className="unir-clientes__explicacao__lista">
          <li>Orçamentos e planos de tratamento</li>
          <li>Avaliações e fichas clínicas</li>
          <li>Agendamentos</li>
          <li>Conta corrente, parcelas e histórico financeiro</li>
          <li>Documentos, ficheiros e fotografias</li>
          <li>Anamneses e prescrições</li>
        </ul>

        <h4 className="unir-clientes__explicacao__subtitulo">
          O que acontece ao cliente eliminado
        </h4>
        <p>
          Após a transferência de todos os dados, o registo do{" "}
          <strong>cliente a eliminar</strong> é removido permanentemente da base
          de dados. Esta operação <strong>não pode ser revertida</strong>.
        </p>

        <div className="unir-clientes__aviso">
          <span className="unir-clientes__aviso__icone">⚠</span>
          <span>
            Verifica sempre os dois perfis antes de correr o script. Em caso de
            dúvida, exporta ou regista os dados de ambos antes de prosseguir.
          </span>
        </div>

        <h4 className="unir-clientes__explicacao__subtitulo">
          Como identificar os clientes
        </h4>
        <p>
          Podes inserir o <strong>ID do cliente</strong> (24 caracteres
          hexadecimais, visível no URL da ficha do cliente) ou o{" "}
          <strong>link completo</strong> da ficha, por exemplo:
        </p>
        <code className="unir-clientes__explicacao__codigo">
          https://app.healthtechcv.com/clientes/507f1f77bcf86cd799439011
        </code>
      </div>

      {/* ── Formulário ──────────────────────────────────────────────── */}
      <div className="unir-clientes__formulario">
        <div className="unir-clientes__campo">
          <label className="unir-clientes__label unir-clientes__label--green">
            Cliente a manter
          </label>
          <p className="unir-clientes__label-desc">
            Este registo ficará activo. Todos os dados do cliente a eliminar
            serão transferidos para aqui.
          </p>
          <input
            className="unir-clientes__input"
            type="text"
            placeholder="ID ou link completo da ficha do cliente"
            value={clienteAManter}
            onChange={(e) => {
              setClienteAManter(e.target.value);
              setErroValidacao(null);
              setResultado(null);
            }}
            disabled={isLoading || confirmando}
          />
          {idManter && (
            <span className="unir-clientes__id-extraido unir-clientes__id-extraido--manter">
              ID detectado: {idManter}
            </span>
          )}
        </div>

        <div className="unir-clientes__campo">
          <label className="unir-clientes__label unir-clientes__label--red">
            Cliente a eliminar
          </label>
          <p className="unir-clientes__label-desc">
            Este registo será removido após a transferência dos dados.
          </p>
          <input
            className="unir-clientes__input unir-clientes__input--eliminar"
            type="text"
            placeholder="ID ou link completo da ficha do cliente"
            value={clienteAEliminar}
            onChange={(e) => {
              setClienteAEliminar(e.target.value);
              setErroValidacao(null);
              setResultado(null);
            }}
            disabled={isLoading || confirmando}
          />
          {idEliminar && (
            <span className="unir-clientes__id-extraido unir-clientes__id-extraido--eliminar">
              ID detectado: {idEliminar}
            </span>
          )}
        </div>

        {erroValidacao && (
          <p className="unir-clientes__erro-validacao">{erroValidacao}</p>
        )}

        {!confirmando && (
          <button
            className="unir-clientes__btn unir-clientes__btn--preparar"
            onClick={handlePrepararConfirmacao}
            disabled={isLoading}
          >
            Preparar execução
          </button>
        )}

        {/* ── Painel de confirmação ────────────────────────────────── */}
        {confirmando && (
          <div className="unir-clientes__confirmacao">
            <p className="unir-clientes__confirmacao__texto">
              Tens a certeza? Esta operação é <strong>irreversível</strong>.
            </p>
            <div className="unir-clientes__confirmacao__resumo">
              <div className="unir-clientes__confirmacao__linha">
                <span className="unir-clientes__confirmacao__etiqueta">
                  Manter
                </span>
                <code>{idManter}</code>
              </div>
              <div className="unir-clientes__confirmacao__linha unir-clientes__confirmacao__linha--eliminar">
                <span className="unir-clientes__confirmacao__etiqueta">
                  Eliminar
                </span>
                <code>{idEliminar}</code>
              </div>
            </div>
            <div className="unir-clientes__confirmacao__acoes">
              <button
                className="unir-clientes__btn unir-clientes__btn--cancelar"
                onClick={handleCancelar}
                disabled={isLoading}
              >
                Cancelar
              </button>
              <button
                className="unir-clientes__btn unir-clientes__btn--confirmar"
                onClick={handleCorrer}
                disabled={isLoading}
              >
                {isLoading ? "A processar…" : "Confirmar e executar"}
              </button>
            </div>
          </div>
        )}

        {/* ── Resultado ───────────────────────────────────────────── */}
        {resultado && (
          <div
            className={`unir-clientes__resultado ${
              resultado.sucesso
                ? "unir-clientes__resultado--sucesso"
                : "unir-clientes__resultado--erro"
            }`}
          >
            {resultado.sucesso ? (
              <>
                <strong>Concluído com sucesso.</strong>
                {resultado.dados?.mensagem && <p>{resultado.dados.mensagem}</p>}
              </>
            ) : (
              <>
                <strong>Erro ao executar o script.</strong>
                <p>{resultado.mensagem}</p>
              </>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

export default UnirClientes;
